import { Correction, CorrectionType } from '@mews-commander/data-access';
import { LocalizedDateTimeFromUtc, SpacedForwardSlash } from '@mews-commander/ui';
import { Body } from '@optimus-web/core';
import { MatrixMapping } from '@mews/b2b-ui';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { UnicodeEntity } from '@mews/enums';
import { WithEnterpriseId, WithOwnerId } from '@mews-commander/types';
import { ResponsiveTable } from '@mews-commander/ui/legacy';
import { ProfileLink } from '../../../../../../components';
import { CorrectionBillLink } from '../CorrectionBillLink';
import { CorrectionsCorrectedBillLink } from './CorrectionsCorrectedBillLink';

const correctionTypeToTextKey: Record<CorrectionType, Mews.TextKey> = {
    [CorrectionType.Cancellation]: TextKey.RemovedItem,
    [CorrectionType.CreditNote]: TextKey.CreditNote,
    [CorrectionType.Edit]: TextKey.EditedItem,
    [CorrectionType.ReceivablePaymentsBalance]: TextKey.ReceivablePaymentsBalance,
    [CorrectionType.Reinstatement]: TextKey.Reinstatement,
};

const createMapping = ({ ownerId, enterpriseId }: WithOwnerId & WithEnterpriseId): Array<MatrixMapping<Correction>> => [
    {
        valueKey: 'id',
        title: <LocalizedText textKey={TextKey.CorrectionName} />,
        render: ({ id, name }) =>
            name ? <CorrectionBillLink billId={id} label={name} /> : <Body>{UnicodeEntity.Dash}</Body>,
    },
    {
        valueKey: 'createdUtc',
        title: <LocalizedText textKey={TextKey.Created} />,
        render: ({ createdUtc }: Correction) => <LocalizedDateTimeFromUtc utcString={createdUtc} />,
    },
    {
        valueKey: 'creatorProfileId',
        title: <LocalizedText textKey={TextKey.CorrectedBy} />,
        render: ({ creatorProfileId }: Correction) => <ProfileLink profileId={creatorProfileId} />,
    },
    {
        valueKey: 'reason',
        title: <LocalizedText textKey={TextKey.Reason} />,
        columnWidth: '25%',
    },
    {
        valueKey: 'correctionType',
        title: <LocalizedText textKey={TextKey.CorrectionType} />,
        render: ({ correctionType }: Correction) =>
            correctionType && correctionTypeToTextKey[correctionType] ? (
                <LocalizedText textKey={correctionTypeToTextKey[correctionType]} />
            ) : null,
    },
    {
        valueKey: 'correctedBillId',
        title: <LocalizedText textKey={TextKey.CorrectedBill} />,
        render: ({ correctedBillId }: Correction) =>
            correctedBillId ? (
                <CorrectionsCorrectedBillLink
                    correctedBillId={correctedBillId}
                    ownerId={ownerId}
                    enterpriseId={enterpriseId}
                />
            ) : (
                <>
                    <LocalizedText textKey={TextKey.UnpaidBillItems} />
                    {SpacedForwardSlash}
                    <LocalizedText textKey={TextKey.DepositPlural} />
                </>
            ),
    },
];

interface CorrectionsTableProps extends WithOwnerId, WithEnterpriseId {
    bills: Array<Correction>;
}

export const CorrectionsTable = ({ bills, ownerId, enterpriseId }: CorrectionsTableProps) => (
    <ResponsiveTable mapping={createMapping({ ownerId, enterpriseId })} source={bills} fixedHeader />
);
